import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { UserBadges } from "@/components/UserBadges";
import { NftInteractionsBar } from "@/components/marketplace/NftInteractionsBar";
import { PurchaseConfirmationModal } from "@/components/marketplace/PurchaseConfirmationModal";
import { TrendingUp, Clock, ShieldCheck, Image, Coins, DollarSign, ShoppingCart, Loader2, ExternalLink, Waves, PauseCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { useEmonRate } from "@/contexts/EmonPriceContext";
import { useEmonTokenTransfer } from "@/hooks/useEmonTokenTransfer";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { SolanaExplorerLink } from "@/components/SolanaExplorerLink";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";

// Raydium swap URL for buying EMON
const RAYDIUM_SWAP_URL = "https://raydium.io/swap/?inputMint=sol&outputMint=76kfPfv218KhNQGx7gpC1GR9UWLZHBGSimhyK79ZpUMP";

import { priceToUsd } from "@/lib/priceUtils";

interface NFTCardProps {
  nft: MarketplaceNFT;
  showTrending?: boolean;
  onBuySuccess?: () => void | Promise<void>;
  showHoldingBadge?: boolean; // Show "Holding" badge when not listed
}

export function NFTCard({ nft, showTrending = false, onBuySuccess, showHoldingBadge = true }: NFTCardProps) {
  const displayPrice = nft.current_price || nft.price;
  const trendingScore = nft.sales_count * 10 + nft.total_earned * 100;
  
  // Use real-time EMON price hook with conversion utilities
  const { rate: emonRate, usdToEmon } = useEmonRate();
  
  // USD is the reference value (stable) - handle both legacy SOL and new USD values
  const usdPrice = priceToUsd(displayPrice);
  // EMON is calculated from USD using real-time rate (variable)
  const emonPrice = usdToEmon(usdPrice);
  const formatUsd = (v: number) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);

  const { connected, publicKey } = useWalletSafe();
  const { buyNft, loading: programLoading, getExplorerUrl } = useEmonTokenTransfer();
  
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [buying, setBuying] = useState(false);
  const [lastTxSignature, setLastTxSignature] = useState<string | null>(null);

  const isOwner = publicKey?.toBase58() === nft.owner_wallet;
  const isHolding = nft.is_listed === false;

  const handleBuyClick = () => {
    if (!connected) {
      toast({ title: "Connect your wallet first", variant: "destructive" });
      return;
    }
    if (isOwner) {
      toast({ title: "You already own this NFT", variant: "destructive" });
      return;
    }
    if (isHolding) {
      toast({ title: "This NFT is currently not for sale", variant: "destructive" });
      return;
    }
    setShowBuyDialog(true);
  };

  // Handler receives frozen rates from the modal
  const handleConfirmBuy = async (frozenEmonRate: number, frozenSolPrice: number) => {
    console.log("🛒 Starting purchase with FROZEN rates:", { frozenEmonRate, frozenSolPrice, nftId: nft.id });
    setBuying(true);
    let ok = false;
    try {
      // Pass frozen rate to buyNft for consistent pricing
      const result = await buyNft(nft.id, nft.owner_wallet, usdPrice, frozenEmonRate);
        if (result) {
          ok = true;
          setLastTxSignature(result.signature);

          if (result.synced) {
            toast({
              title: "🎉 NFT Purchased!",
              description: `You are now the owner! Next price: $${formatUsd(result.newPriceUsd)}`,
            });
          } else {
            toast({
              title: "✅ Payment confirmed",
              description:
                result.syncError ||
                "Syncing ownership and price… you'll see the updated Owner and new Buy price in a few seconds.",
              variant: "destructive",
            });
          }

          // Immediate refresh + delayed refresh to ensure DB sync
          await onBuySuccess?.();
          setTimeout(() => {
            onBuySuccess?.();
          }, 1500);
        }
    } catch (error: any) {
      console.error("❌ Purchase error:", error);
      toast({
        title: "Purchase failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBuying(false);
      if (ok) setShowBuyDialog(false);
    }
  };

  return (
    <>
      <Card className="group overflow-hidden bg-card/80 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1 h-full flex flex-col">
        <div className="aspect-square relative overflow-hidden bg-muted">
          {nft.image_url ? (
            <img
              src={nft.image_url}
              alt={nft.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Image className="w-12 h-12 text-muted-foreground" />
            </div>
          )}
          
          {/* Overlay badges */}
          <div className="absolute top-1 sm:top-2 left-1 sm:left-2 flex flex-col gap-0.5 sm:gap-1">
            {/* Holding badge - shown when NFT is not listed */}
            {showHoldingBadge && nft.is_listed === false && (
              <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                <PauseCircle className="w-2.5 sm:w-3 h-2.5 sm:h-3 sm:mr-1" />
                <span className="hidden sm:inline">Holding</span>
              </Badge>
            )}
            {showTrending && trendingScore > 0 && (
              <Badge variant="default" className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                <TrendingUp className="w-2.5 sm:w-3 h-2.5 sm:h-3 mr-0.5 sm:mr-1" />
                <span className="hidden sm:inline">Hot</span>
              </Badge>
            )}
            {nft.image_verified && (
              <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
                <ShieldCheck className="w-2.5 sm:w-3 h-2.5 sm:h-3 sm:mr-1" />
                <span className="hidden sm:inline">Verified</span>
              </Badge>
            )}
            {/* Bonding Curve badge with tooltip - hidden on mobile */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="bg-primary/20 text-primary border-primary/50 text-[10px] sm:text-xs cursor-help hidden sm:flex px-1.5 sm:px-2 py-0.5">
                    <Waves className="w-2.5 sm:w-3 h-2.5 sm:h-3 mr-0.5 sm:mr-1" />
                    Flow
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[200px]">
                  <p className="text-xs font-medium">Bonding Curve Pricing</p>
                  <p className="text-xs text-muted-foreground">Anti-manipulation rules enforced</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Price tags - USD (reference) + EMON (calculated) */}
          <div className="absolute bottom-1 sm:bottom-2 right-1 sm:right-2 flex flex-col gap-0.5 sm:gap-1 items-end">
            {/* USD is primary - stable reference */}
            <Badge className="bg-green-500/90 text-white border-0 font-bold gap-0.5 sm:gap-1 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
              <DollarSign className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
              {formatUsd(usdPrice)}
            </Badge>
            {/* EMON is secondary - calculated from USD - hidden on mobile */}
            <Badge variant="secondary" className="bg-background/90 text-muted-foreground border-0 text-[9px] sm:text-xs gap-0.5 hidden sm:flex">
              <Coins className="w-2 sm:w-2.5 h-2 sm:h-2.5 text-primary" />
              ≈ {emonPrice.toLocaleString()} EMON
            </Badge>
          </div>

          {/* Buy button overlay on hover - desktop only */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:flex items-center justify-center">
            <Button
              variant="hero"
              size="lg"
              className="gap-2 shadow-xl"
              onClick={handleBuyClick}
              disabled={isOwner || isHolding || programLoading}
            >
              {isOwner ? (
                "You Own This"
              ) : isHolding ? (
                "Not For Sale"
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Buy {formatUsd(usdPrice)}$
                </>
              )}
            </Button>
          </div>
        </div>
        
        <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3 flex-1 flex flex-col">
          <div>
            <h3 className="font-semibold text-primary truncate group-hover:text-accent transition-colors text-sm sm:text-base">
              {nft.name}
            </h3>
            {nft.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-1 sm:line-clamp-2 hidden sm:block">
                {nft.description}
              </p>
            )}
            {/* Creator - IMMUTABLE, never changes after creation */}
            <div className="flex items-center gap-1 mt-1">
              <p className="text-[10px] sm:text-xs truncate">
                <span className="text-muted-foreground">by</span>{" "}
                <span className="text-orange-500 font-medium">
                  {nft.creator_name || "Creator"}
                </span>
              </p>
            </div>
            {/* Owner - dynamic, changes after each purchase */}
            <div className="flex items-center gap-1 sm:gap-2 mt-0.5">
              <p className="text-[10px] sm:text-xs truncate flex-1">
                <span className="text-muted-foreground">Owner:</span>{" "}
                {nft.owner_id ? (
                  <Link 
                    to={`/profile/${nft.owner_id}`}
                    className="text-primary font-medium hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {nft.owner_name || "Owner"}
                  </Link>
                ) : (
                  <span className="text-primary font-medium">
                    {nft.owner_name || "Owner"}
                  </span>
                )}
              </p>
              <UserBadges walletAddress={nft.owner_wallet} maxDisplay={1} />
            </div>
          </div>

          <div className="flex items-center justify-between text-[10px] sm:text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
              <span className="hidden sm:inline">{nft.created_at ? formatDistanceToNow(new Date(nft.created_at), { addSuffix: true }) : "Unknown"}</span>
              <span className="sm:hidden">{nft.created_at ? formatDistanceToNow(new Date(nft.created_at), { addSuffix: false }) : "?"}</span>
            </div>
            {nft.sales_count > 0 && (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <TrendingUp className="w-2.5 sm:w-3 h-2.5 sm:h-3 text-green-500" />
                {nft.sales_count}
              </div>
            )}
          </div>

          {/* Spacer to push content to bottom */}
          <div className="flex-1" />

          {/* Interactions: Likes, Tips, Share */}
          <NftInteractionsBar
            nftId={nft.id}
            nftName={nft.name}
            nftImageUrl={nft.image_url}
            ownerWallet={nft.owner_wallet}
            ownerName={nft.owner_name}
            priceUsd={usdPrice}
            priceEmon={emonPrice}
            variant="compact"
          />

          {/* Mobile buy button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 md:hidden"
            onClick={handleBuyClick}
            disabled={isOwner || isHolding || programLoading}
          >
            {isOwner ? (
              "You Own This"
            ) : isHolding ? (
              "Not For Sale"
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                Buy {formatUsd(usdPrice)}$
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Buy Confirmation Modal with Price Freeze */}
      <PurchaseConfirmationModal
        open={showBuyDialog}
        onOpenChange={setShowBuyDialog}
        onConfirm={handleConfirmBuy}
        loading={buying}
        nft={{
          name: nft.name,
          image_url: nft.image_url,
          owner_wallet: nft.owner_wallet,
          owner_name: nft.owner_name,
        }}
        priceUsd={usdPrice}
      />

      {/* Transaction Success Toast with Explorer Link */}
      {lastTxSignature && (
        <div className="hidden">
          <a
            href={getExplorerUrl(lastTxSignature, "tx")}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Explorer
          </a>
        </div>
      )}
    </>
  );
}

export function NFTCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <Skeleton className="aspect-square" />
      <CardContent className="p-4 space-y-3">
        <div>
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3 w-1/2 mt-2" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
      </CardContent>
    </Card>
  );
}
